// timeline-slider.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import Flickity from "flickity";

class TimelineSlider {
    constructor(elementOrSelector) {
        // Selector oder direktes Element akzeptieren
        this.root = typeof elementOrSelector === "string"
            ? document.querySelector(elementOrSelector)
            : elementOrSelector;

        if (!this.root) {
            console.warn("TimelineSlider: root element not found.", elementOrSelector);
            return;
        }

        // Slider-Container
        this.carouselEl = this.root.querySelector(".timeline-slider");
        if (!this.carouselEl) {
            console.warn("TimelineSlider: .timeline-slider not found within root.", this.root);
            return;
        }

        // Prev / Next Buttons
        this.nextArrow = this.root.querySelector(".js-slider-button-next");
        this.prevArrow = this.root.querySelector(".js-slider-button-prev");

        if (!this.nextArrow || !this.prevArrow) {
            console.warn("TimelineSlider: missing arrow buttons.", this.root);
        }

        // Flickity Init
        this.flkty = new Flickity(this.carouselEl, {
            adaptiveHeight: false,
            cellAlign: "left",
            draggable: true,
            pageDots: false,
            prevNextButtons: false,
            initialIndex: 0,
            wrapAround: false
        });

        this.slideCount = 0;

        // Ready Event
        this.flkty.on("ready", () => {
            this.slideCount = this.flkty.slides.length;

            this.setCurrentSlide(this.flkty.selectedIndex);
            this.updatePassedSlides(this.flkty.selectedIndex);

            ScrollTrigger.refresh();
        });

        // Refresh Flickity on settle
        this.flkty.on("settle", () => ScrollTrigger.refresh());

        // On change
        this.flkty.on("change", (index) => this.onSlideChange(index));

        // Button Events
        this.prevArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.previous();
        });

        this.nextArrow?.addEventListener("click", (event) => {
            event.preventDefault();
            this.flkty.next();
        });

        // Initial Refresh after page load
        const init = () => this.refresh();
        if (document.readyState === "complete") init();
        else window.addEventListener("load", init);

        // Resize handling
        window.addEventListener("resize", () => {
            this.refresh();
        });
    }

    onSlideChange(index) {
        this.setCurrentSlide(index);
        this.updatePassedSlides(index);
    }

    updatePassedSlides(activeIndex) {
        if (!this.flkty?.cells) return;

        this.flkty.cells.forEach((cell, i) => {
            const slide = cell.element;
            if (!slide) return;

            if (i < activeIndex) {
                slide.classList.add("is-passed");
            } else {
                slide.classList.remove("is-passed");
            }
        });
    }

    setCurrentSlide(index) {
        if (!this.prevArrow || !this.nextArrow) return;

        if (index === 0) {
            this.prevArrow.classList.add("is-disabled");
        } else {
            this.prevArrow.classList.remove("is-disabled");
        }

        if (index === this.slideCount - 1) {
            this.nextArrow.classList.add("is-disabled");
        } else {
            this.nextArrow.classList.remove("is-disabled");
        }
    }

    refresh() {
        if (!this.flkty) return;

        this.flkty.reloadCells();
        this.flkty.resize();
        this.flkty.reposition();
        this.slideCount = this.flkty.slides.length;

        this.setCurrentSlide(this.flkty.selectedIndex);
        this.updatePassedSlides(this.flkty.selectedIndex);
    }

    static mountAll(selector = ".js-timeline-slider") {
        document.querySelectorAll(selector).forEach((el) => new TimelineSlider(el));
    }
}

export default TimelineSlider;
